import { useCallback, useEffect, useState } from 'react';
import {
  verticalPricingApi,
  type AdminTier,
  type TierUpdatePayload,
  type VerticalPricingResponse,
} from '@/api/verticalPricing';

// Operator pricing admin (VERTICAL-PRICING-ADMIN-1, Phase 1). List a vertical's
// tiers; edit price / included-credits / the per-vertical peg. The hard invariant
// — the price on the card == the Paddle charge — is enforced backend-side and
// surfaced inline; production price changes are Director-gated (confirm).

function errorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(error.message) as { detail?: unknown; message?: unknown };
    const detail = parsed.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // FastAPI request-validation errors: a list of { loc, msg, ... }.
      const messages = detail
        .map((item) => {
          if (!item || typeof item !== 'object' || typeof (item as { msg?: unknown }).msg !== 'string') return null;
          const loc = (item as { loc?: unknown }).loc;
          const field = Array.isArray(loc) ? String(loc[loc.length - 1]) : '';
          return field ? `${field}: ${(item as { msg: string }).msg}` : (item as { msg: string }).msg;
        })
        .filter((m): m is string => m !== null);
      if (messages.length > 0) return messages.join('; ');
    }
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const m = (detail as { message?: unknown }).message;
      if (typeof m === 'string') return m;
    }
    if (typeof parsed.message === 'string') return parsed.message;
  } catch {
    /* ApiError.message may be a plain body string */
  }
  return error.message;
}

function money(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function PricingTab({ vertical }: { vertical: string }) {
  const [data, setData] = useState<VerticalPricingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [grantMode, setGrantMode] = useState<'compatibility' | 'price_default' | 'fixed_total'>('compatibility');
  const [creditsInput, setCreditsInput] = useState('');
  const [productIdInput, setProductIdInput] = useState('');
  const [confirmInput, setConfirmInput] = useState(false);

  const [editingPeg, setEditingPeg] = useState(false);
  const [pegInput, setPegInput] = useState('');

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const isProd = data?.environment === 'production';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await verticalPricingApi.getPricing(vertical);
      setData(resp);
    } catch (error) {
      if (error instanceof Error && error.message.includes('403')) {
        setAccessError(`You are not authorized to administer the ${vertical} vertical.`);
      } else {
        setLoadError(errorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  }, [vertical]);

  useEffect(() => {
    setAccessError(null);
    setEditingKey(null);
    setEditingPeg(false);
    void load();
  }, [load]);

  const beginEdit = (tier: AdminTier) => {
    setEditingKey(tier.tier_key);
    setGrantMode(tier.credit_grant_mode ?? 'compatibility');
    setPriceInput(String(tier.monthly_price_usd));
    setCreditsInput(tier.included_credits != null ? String(tier.included_credits) : '');
    setProductIdInput('');
    setConfirmInput(false);
    setActionError(null);
    setActionMessage(null);
  };

  const saveTier = async (tier: AdminTier) => {
    setActionLoading(tier.tier_key);
    setActionError(null);
    setActionMessage(null);
    try {
      const payload: TierUpdatePayload = {};
      if (priceInput.trim() !== '') payload.monthly_price_usd = priceInput.trim();
      // The typed text goes to the API as a string, not through Number(): a JS number
      // holds ~15 significant digits, so a long input would be rounded in the browser
      // before the API could refuse it. The API decodes the count as an exact decimal.
      if (grantMode !== 'price_default' && creditsInput.trim() !== '') payload.included_credits = creditsInput.trim();
      if (grantMode !== 'compatibility') payload.credit_grant_mode = grantMode;
      if (productIdInput.trim() !== '') payload.product_id = productIdInput.trim();
      if (isProd) payload.confirm = confirmInput;
      if (payload.monthly_price_usd === undefined && payload.included_credits === undefined && payload.credit_grant_mode === undefined) {
        throw new Error(JSON.stringify({ detail: { message: 'Enter a price and/or a credit count.' } }));
      }
      const res = await verticalPricingApi.updateTier(vertical, tier.tier_key, payload);
      const repointed = res.repointed_paddle_price_id
        ? ` Repointed Paddle price → ${res.repointed_paddle_price_id} (card == charge verified).`
        : '';
      setActionMessage(`Updated ${tier.display_name}.${repointed}`);
      setEditingKey(null);
      await load();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  const savePeg = async () => {
    setActionLoading('peg');
    setActionError(null);
    setActionMessage(null);
    try {
      const rate = Number(pegInput);
      if (!Number.isFinite(rate) || rate <= 0) {
        throw new Error(JSON.stringify({ detail: { message: 'Peg must be a number greater than 0.' } }));
      }
      const res = await verticalPricingApi.updatePeg(vertical, {
        credit_exchange_rate: rate,
        confirm: isProd ? true : undefined,
      });
      setActionMessage(
        `Peg updated: 1 credit = $${res.credit_exchange_rate} (was $${res.previous_credit_exchange_rate}).`,
      );
      setEditingPeg(false);
      await load();
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setActionLoading(null);
    }
  };

  if (accessError) {
    return (
      <div className="p-6 font-mono text-sm text-gray-400 space-y-3">
        <div className="text-xs uppercase tracking-wider text-gray-600">Pricing</div>
        <div className="border border-red-500/30 bg-red-500/5 text-red-300 rounded p-3">{accessError}</div>
      </div>
    );
  }

  return (
    <div className="p-6 font-mono text-sm text-gray-200">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">Pricing</div>
          <div className="text-[11px] text-gray-600 mt-1">
            Edit each tier's price and included credits. A price change creates a new Paddle price and
            repoints checkout — the card price is enforced to equal the Paddle charge.
          </div>
        </div>
        {data && (
          <div
            className={`text-[11px] px-2 py-1 rounded border ${
              isProd
                ? 'border-orange-500/40 text-orange-300 bg-orange-500/5'
                : 'border-gray-700 text-gray-400'
            }`}
            title="Paddle environment"
          >
            {data.environment}
          </div>
        )}
      </div>

      {isProd && (
        <div className="mb-4 border border-orange-500/30 bg-orange-500/5 text-orange-300 text-[11px] rounded p-3">
          Production — price changes are Director-gated and require confirmation before they affect real billing.
        </div>
      )}

      {actionMessage && (
        <div role="status" className="mb-4 border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14] text-xs rounded p-3">
          {actionMessage}
        </div>
      )}
      {actionError && (
        <div role="alert" className="mb-4 border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded p-3">
          {actionError}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Loading {vertical} pricing…</p>
      ) : loadError ? (
        <div className="text-red-400 text-xs">
          <p>Failed to load {vertical} pricing.</p>
          <p className="text-gray-500 mt-1">{loadError}</p>
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Per-vertical peg */}
          <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Credit peg (this vertical)</div>
                <div className="text-lg text-gray-100 mt-1">1 credit = ${data.credit_exchange_rate}</div>
                <div className="text-[10px] text-gray-600 mt-1">
                  USD granted per included credit — the dial that converts a tier's credit count into the dollars it grants.
                </div>
              </div>
              {!editingPeg ? (
                <button
                  type="button"
                  onClick={() => {
                    setEditingPeg(true);
                    setPegInput(String(data.credit_exchange_rate));
                    setActionError(null);
                    setActionMessage(null);
                  }}
                  className="px-3 py-1.5 text-xs border border-gray-700 rounded text-gray-300 hover:bg-gray-800/40"
                >
                  Edit peg
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    aria-label="Credit peg"
                    value={pegInput}
                    onChange={(e) => setPegInput(e.target.value)}
                    inputMode="decimal"
                    className="w-24 bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-gray-100"
                  />
                  <button
                    type="button"
                    disabled={actionLoading !== null}
                    onClick={() => void savePeg()}
                    className="px-3 py-1.5 text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                  >
                    {actionLoading === 'peg' ? 'Saving…' : 'Save peg'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingPeg(false)}
                    className="px-3 py-1.5 text-xs border border-gray-800 rounded text-gray-500 hover:bg-gray-800/40"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tiers */}
          <div className="overflow-auto border border-gray-800 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-900/90 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Tier</th>
                  <th className="text-right px-3 py-2 font-normal">Price / mo</th>
                  <th className="text-right px-3 py-2 font-normal">Credits</th>
                  <th className="text-left px-3 py-2 font-normal">Paddle</th>
                  <th className="text-left px-3 py-2 font-normal">Subscribe-ready</th>
                  <th className="text-right px-3 py-2 font-normal" />
                </tr>
              </thead>
              <tbody>
                {data.tiers.map((tier) => {
                  const isEditing = editingKey === tier.tier_key;
                  return (
                    <tr key={tier.tier_key} className="border-t border-gray-800 align-top">
                      <td className="px-3 py-2">
                        <div className="text-gray-200">{tier.display_name}</div>
                        <div className="text-gray-600">{tier.tier_key}{tier.is_default ? ' · default' : ''}</div>
                      </td>
                      {!isEditing ? (
                        <>
                          <td className="px-3 py-2 text-right text-gray-200">
                            {tier.is_free ? 'Free' : money(tier.monthly_price_usd)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-300">
                            {tier.included_credits != null ? tier.included_credits : '—'}
                            <div className="text-[10px] text-gray-600">{tier.monthly_credits} shown</div>
                          </td>
                          <td className="px-3 py-2 text-gray-500 break-all">{tier.paddle_price_monthly ?? '—'}</td>
                          <td className={`px-3 py-2 ${tier.subscribe_ready ? 'text-[#39FF14]' : 'text-gray-600'}`}>
                            {tier.is_free ? 'free tier' : tier.subscribe_ready ? 'yes' : 'no'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => beginEdit(tier)}
                              className="px-2 py-1 text-[11px] border border-gray-700 rounded text-gray-300 hover:bg-gray-800/40"
                            >
                              Edit
                            </button>
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-2" colSpan={5}>
                          <div className="space-y-2 max-w-md">
                            <div className="grid grid-cols-2 gap-3">
                              <label className="block text-[11px] text-gray-500">
                                Price / month (USD)
                                <input
                                  aria-label="Monthly price"
                                  value={priceInput}
                                  onChange={(e) => setPriceInput(e.target.value)}
                                  inputMode="decimal"
                                  className="mt-1 w-full bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-gray-100"
                                />
                              </label>
                              <label className="block text-[11px] text-gray-500">
                                Monthly credit policy
                                <select aria-label="Monthly credit policy" value={grantMode}
                                  onChange={(e) => setGrantMode(e.target.value as typeof grantMode)}>
                                  {grantMode === 'compatibility' && <option value="compatibility">Keep existing policy</option>}
                                  {!tier.is_free && <option value="price_default">Default: one credit per dollar</option>}
                                  {!tier.is_free && <option value="fixed_total">Fixed total</option>}
                                </select>
                                <span> {tier.monthly_credits} credits currently shown by server</span>
                              </label>
                              <label className="block text-[11px] text-gray-500">
                                Included credits
                                <input
                                  aria-label="Included credits"
                                  disabled={grantMode === 'price_default'}
                                  value={creditsInput}
                                  onChange={(e) => setCreditsInput(e.target.value)}
                                  inputMode="decimal"
                                  className="mt-1 w-full bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-gray-100"
                                />
                              </label>
                            </div>
                            {!tier.paddle_price_monthly && Number(priceInput) > 0 && (
                              <label className="block text-[11px] text-gray-500">
                                Paddle product id (first-time price wiring)
                                <input
                                  aria-label="Paddle product id"
                                  value={productIdInput}
                                  onChange={(e) => setProductIdInput(e.target.value)}
                                  placeholder="pro_…"
                                  className="mt-1 w-full bg-black/40 border border-gray-700 rounded px-2 py-1 text-xs text-gray-100"
                                />
                              </label>
                            )}
                            <div className="text-[10px] text-gray-600">
                              The card price is enforced to equal the Paddle charge — a mismatch is rejected.
                            </div>
                            {isProd && (
                              <label className="flex items-center gap-2 text-[11px] text-orange-300">
                                <input
                                  type="checkbox"
                                  checked={confirmInput}
                                  onChange={(e) => setConfirmInput(e.target.checked)}
                                />
                                Confirm this production price change.
                              </label>
                            )}
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                disabled={actionLoading !== null}
                                onClick={() => void saveTier(tier)}
                                className="px-3 py-1.5 text-xs border border-[#39FF14]/30 rounded text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-50"
                              >
                                {actionLoading === tier.tier_key ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingKey(null)}
                                className="px-3 py-1.5 text-xs border border-gray-800 rounded text-gray-500 hover:bg-gray-800/40"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
